import apiClient from './api';

/**
 * Adress-Suche Suchergebnis
 */
export interface AddressSearchResult {
  query: string;
  source: 'internal' | 'external' | 'combined';
  success: boolean;
  errorMessage?: string;
  totalResults: number;
  responseTimeMs: number;
  addresses: AddressSearchItem[];
}

/**
 * Einzelne Adresse im Suchergebnis
 */
export interface AddressSearchItem {
  id?: string;
  source: string;
  name?: string;
  company?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  confidence: number;
  isInternal: boolean;
  formattedAddress: string;
}

/**
 * Ergebnis des Adressen-Sync zu RAG
 */
export interface SyncAddressesToRagResult {
  mandantId: number;
  syncedAt: string;
  success: boolean;
  message?: string;
  errorMessage?: string;
  totalAddresses: number;
  syncedCount: number;
}

/**
 * AddressSearch Service
 * Sucht Adressen intern (Skeena DB + RAG) und extern (tel.search.ch, local.ch)
 */
const addressSearchService = {
  /**
   * Sucht Adressen in der internen Skeena-Datenbank
   */
  searchInternal: async (query: string, maxResults: number = 10): Promise<AddressSearchResult> => {
    const response = await apiClient.get<AddressSearchResult>('/addresssearch/internal', {
      params: { query, maxResults }
    });
    return response.data;
  },

  /**
   * Sucht Firmenadressen im Internet (tel.search.ch, local.ch)
   */
  searchExternal: async (
    query: string,
    location?: string,
    maxResults: number = 10
  ): Promise<AddressSearchResult> => {
    const response = await apiClient.get<AddressSearchResult>('/addresssearch/external', {
      params: { query, location, maxResults }
    });
    return response.data;
  },

  /**
   * Kombinierte Suche: erst intern, dann extern
   * Interne Ergebnisse haben Priorität und werden zuerst angezeigt
   */
  searchCombined: async (
    query: string,
    location?: string,
    maxResults: number = 10
  ): Promise<AddressSearchResult> => {
    const response = await apiClient.get<AddressSearchResult>('/addresssearch/combined', {
      params: { query, location, maxResults }
    });
    return response.data;
  },

  /**
   * Synchronisiert alle Adressen des Mandanten zum RAG-System
   * Dies ermöglicht semantische Suche über Adressen
   */
  syncToRag: async (): Promise<SyncAddressesToRagResult> => {
    const response = await apiClient.post<SyncAddressesToRagResult>('/addresssearch/sync-to-rag');
    return response.data;
  },

  /**
   * Hilfsfunktion: Formatiert eine Adresse für die Anzeige
   */
  formatAddress: (address: AddressSearchItem): string => {
    const parts: string[] = [];

    if (address.name) parts.push(address.name);
    if (address.company && address.company !== address.name) parts.push(address.company);
    if (address.street) parts.push(address.street);

    const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);

    if (address.country && address.country !== 'Schweiz') parts.push(address.country);

    return parts.join(', ');
  },

  /**
   * Hilfsfunktion: Formatiert eine Adresse als mehrzeiligen Brief-Header
   */
  formatAddressForLetter: (address: AddressSearchItem): string => {
    const lines: string[] = [];

    if (address.company) lines.push(address.company);
    if (address.name) lines.push(address.name);
    if (address.street) lines.push(address.street);

    const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);

    if (address.country && address.country !== 'Schweiz') lines.push(address.country);

    return lines.join('\n');
  },

  /**
   * Hilfsfunktion: Konvertiert Suchergebnis zu CreateAdresseDto
   */
  toCreateAdresseDto: (item: AddressSearchItem): {
    anrede: string;
    vorname: string;
    nachname: string;
    firma?: string;
    strasse?: string;
    hausnummer?: string;
    plz?: string;
    ort?: string;
    land?: string;
    email?: string;
    telefon?: string;
  } => {
    // Versuche Name in Vor- und Nachname zu splitten
    let vorname = '';
    let nachname = '';

    if (item.name) {
      const nameParts = item.name.trim().split(' ');
      if (nameParts.length >= 2) {
        vorname = nameParts.slice(0, -1).join(' ');
        nachname = nameParts[nameParts.length - 1];
      } else {
        nachname = item.name;
      }
    }

    // Strasse und Hausnummer splitten
    let strasse = item.street || undefined;
    let hausnummer: string | undefined;

    if (strasse) {
      const streetMatch = strasse.match(/^(.+?)\s+(\d+[a-zA-Z]?)$/);
      if (streetMatch) {
        strasse = streetMatch[1];
        hausnummer = streetMatch[2];
      }
    }

    return {
      anrede: '',  // Muss vom Benutzer ausgefüllt werden
      vorname,
      nachname,
      firma: item.company || undefined,
      strasse,
      hausnummer,
      plz: item.postalCode || undefined,
      ort: item.city || undefined,
      land: item.country || 'Schweiz',
      email: item.email || undefined,
      telefon: item.phone || undefined,
    };
  }
};

export default addressSearchService;
